/**
 * Focus onto a node's card, at its title — the place a reader who asked for the
 * card starts reading it.
 *
 * Two doors open a card without a pointer and left focus behind them. Enter on
 * the node tree ("Enter opens a node in the thread") opened it in the thread and
 * kept focus on the tree row, so a screen reader heard the row turn selected and
 * then nothing of the card it had asked for; and the folded card's "Open card"
 * strip unmounted under the key that pressed it, dropping focus on the page
 * body. Both now seat focus on the card's title, which is focusable for this and
 * never a tab stop (`Statement`).
 *
 * The card can mount a frame or more after the press — a selection opens the
 * column, and a phone raises its sheet — so this waits a few frames for a title
 * that is on the page and showing, the way `seatWhenFilled` waits for a form.
 * Without scrolling: a new selection seats its card at the head of the feed
 * already (D157), and a focus scroll would fight it.
 */

const SEAT_FRAMES = 12

/**
 * Focus onto the graph's node list, at its landmark — for a phone that has just
 * pressed "Watch a worked example".
 *
 * The first-run sheet opens over nothing, so its own restore leaves focus on the
 * page body, and on a desktop the door hands the caret to the Ask box instead
 * (`sigCaret`). A phone can take neither of the two obvious targets. The Ask box
 * raises the soft keyboard over the example the reader asked to watch; a row of
 * the tree eases the camera to its node (D160), which fights the unfurl the
 * camera is following. The list's landmark does neither: it is not a field, it
 * hovers nothing, and it is where the example IS for a screen reader — "Nodes in
 * this exploration", its help, and one swipe on the first row. Letters keep
 * working from it (it sits inside `.labels`, D161).
 *
 * Only when focus was actually lost — somebody who has already moved on keeps
 * where they went — and a frame late, after the closing sheet's own restore.
 */
export function focusNodeList(): void {
  requestAnimationFrame(() => {
    const here = document.activeElement
    if (here && here !== document.body) return
    document.querySelector<HTMLElement>('[data-node-list]')?.focus({ preventScroll: true })
  })
}

export function focusCardTitle(nodeId: string): void {
  let frames = 0
  const seat = () => {
    // `data-card-title`, which `Statement` writes on the title with its node's id.
    const title = [...document.querySelectorAll<HTMLElement>('[data-card-title]')].find(
      (el) => el.dataset.cardTitle === nodeId && el.getClientRects().length > 0,
    )
    if (title) title.focus({ preventScroll: true })
    else if (++frames < SEAT_FRAMES) requestAnimationFrame(seat)
  }
  requestAnimationFrame(seat)
}

/** Frames a hand-off waits for its press to leave the page: the row goes in the
 *  commit its store write causes, a frame or two after the press at most. */
const GONE_FRAMES = 30

/**
 * Focus onto the pass a press has just started, at its message's head — for a
 * press whose row the pass itself replaces.
 *
 * The presses under an answer stand only while that answer is the last thing in
 * the thread (D42), so a paid one takes its own row away the moment its pass
 * begins: the key that pressed it was left on the page body, and a screen reader
 * heard the pass start from nowhere. What replaced the row is the pass's message,
 * in the same place, and it outlives the pass — a Stop would unmount under the
 * reader again as the pass lands. Only once the pressed button is gone, only
 * while focus really is lost, and without scrolling: the thread follows the
 * pass's tail already.
 */
export function focusLivePass(pressed: HTMLElement): void {
  let frames = 0
  const seat = () => {
    if (pressed.isConnected) {
      if (++frames < GONE_FRAMES) requestAnimationFrame(seat)
      return
    }
    const here = document.activeElement
    if (here && here !== document.body) return
    document
      .querySelector<HTMLElement>('.feed-pass[data-live] [data-pass-head]')
      ?.focus({ preventScroll: true })
  }
  requestAnimationFrame(seat)
}

/**
 * The press that opened a card's rewrite editor, when it was a press — the
 * composer's Record mode (D347). The editor opens on the card, away from it, and
 * closing it (Cancel, or its paid press) took the focused button away: focus goes
 * back to the press that opened it, the way a dialog hands it back. Held for one
 * opening; the E key opens the editor without a press, and then focus goes to the
 * card's title instead.
 */
let rewriteOpener: HTMLElement | null = null

export function noteRewriteOpener(el: HTMLElement): void {
  rewriteOpener = el
}

export function takeRewriteOpener(): HTMLElement | null {
  const el = rewriteOpener
  rewriteOpener = null
  return el?.isConnected ? el : null
}
